import React, { useEffect, useRef, useState, useMemo } from "react";
import { Box, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { format, startOfWeek } from "date-fns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { captureAndDownloadCharts } from "../CaptureCharts/captureAndDownloadCharts.js";
import WeekPicker from "../Pickers/WeekPicker.jsx";

import HeartRate from "./HeartRate.jsx";
import { getHeartRateBySubjectID } from "../../../services/apiCalls.js";
import { calculateWeeksAndMonthsWithData } from "../../../utils/filterDataByDate.jsx";

export default function HeartRateComponent({
  selectedUser,
  tabsValue,
  setLoading,
}) {
  const [heartRateData, setHeartRateData] = useState([]);

  const heartRateOverview = useRef(null);

  const today = new Date();
  const [currentWeek, setCurrentWeek] = useState(today);
  const [todayClickFunc, setTodayClickFunc] = useState(null);

  const { t } = useTranslation("statistics");

  useEffect(() => {
    const fetchUserHeartRateData = async () => {
      try {
        const heartRateData = await getHeartRateBySubjectID(selectedUser);
        const formattedHeartRateData = heartRateData.map((data) => ({
          date: format(new Date(data.date), "yyyy-MM-dd"),
          min: data.min,
          max: data.max,
          average: data.average,
          resting: data.resting,
        }));
        setHeartRateData(formattedHeartRateData);
      } catch (error) {
        console.error(
          "Error when fetching heart rate data in citizens component: ",
          error
        );
      }
    };
    fetchUserHeartRateData();
  }, []);

  const handleTodayClickParent = (func) => {
    if (!todayClickFunc) {
      setTodayClickFunc(() => func);
    }
  };
  const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 });

  const isCurrentWeek =
    weekStart.getTime() === startOfWeek(today, { weekStartsOn: 1 }).getTime();

  const handleWeekChange = (week) => {
    setCurrentWeek(week);
    // console.log("Updated selected week:", week);
  };

  const weeksMonthsWithData = calculateWeeksAndMonthsWithData(heartRateData);
  // console.log("weeksMonthsWithData in heartRateComponent: ", weeksMonthsWithData);

  const weekStartISO = (d) =>
    format(startOfWeek(d, { weekStartsOn: 1 }), "yyyy-MM-dd");

  const weeksWithDataSet = useMemo(() => {
    const s = new Set();
    for (const row of heartRateData) {
      const day = new Date(row.date); // row.date is 'yyyy-MM-dd'
      s.add(weekStartISO(day));
    }
    return s;
  }, [heartRateData]);

  const hasAnyData = heartRateData.length > 0;
  const todayWeekISO = weekStartISO(today);
  const todayWeekHasData = weeksWithDataSet.has(todayWeekISO);

  const showTodayButton = !isCurrentWeek && (!hasAnyData || todayWeekHasData);
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <WeekPicker
          today={today}
          currentCompWeek={currentWeek}
          sendDataToParent={handleTodayClickParent}
          onWeekChange={handleWeekChange}
          weeksWithData={weeksMonthsWithData.weeksWithData}
          monthsWithData={weeksMonthsWithData.monthsWithData}

        />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Button
            onClick={() =>
              captureAndDownloadCharts(
                "HeartRate",
                tabsValue,
                { heartRateOverview },
                setLoading
              )
            }
            sx={{
              maxWidth: "210px",
              "&:hover": {
                backgroundColor: "white",
                color: "rgb(2, 178, 175)",
              },
            }}
          >
            {t("btn.downloadChart")}
          </Button>
        </Box>
        {showTodayButton ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Button
              sx={{
                backgroundColor: "transparent",
                "&:hover": {
                  backgroundColor: "white",
                  color: "rgb(2, 178, 175)",
                },
              }}
              onClick={todayClickFunc}
            >
              {t("btn.today")}
            </Button>
          </Box>
        ) : (
          <Button
            sx={{
              visibility: "hidden",
            }}
          >
            {t("btn.today")}
          </Button>
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: {
              xs: "column",
              sm: "column",
              md: "row",
              lg: "row",
            },
            gap: "10px",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <HeartRate heartRateData={heartRateData} selectedWeek={currentWeek} />
        </Box>
      </Box>
    </LocalizationProvider>
  );
}
